import axios from 'axios';
import SessionService from './SessionService';
import { config } from '../config';
import { apiErrorMessage, SESSION_EXPIRED_MESSAGE } from '../utils/apiErrorMessage';

const API_URL = config?.API_BASE_URL || process.env.REACT_APP_API_URL || 'https://server.mends.pro/api';

export type TextOptimizationType =
  | 'subject'
  | 'body'
  | 'message'
  | 'prompt'
  | 'knowledge_base'
  | 'crm_automation_title'
  | 'crm_automation_instruction'
  | 'followup_prompt'
  | 'document_prompt';

export type OptimizationCapability = 'receptive' | 'campaign' | 'followup' | 'crm';

export interface OptimizeRequest {
  sectorId: number;
  agentId?: number;
  text: string;
  type: TextOptimizationType;
  capability?: OptimizationCapability;
}

export interface OptimizeResponse {
  message: string;
  code: string;
  /** `refused` vem do JSON de schema fechado da IA no backend (status "recusado"); nunca de palavras no texto. */
  data: { optimizedText: string | null; refused?: boolean };
}

const MAX_RETRIES = 3;

export const OPTIMIZE_FALLBACK_MESSAGE = 'Não foi possível melhorar o texto agora. Tente de novo.';
export const OPTIMIZE_REFUSAL_MESSAGE =
  'A IA não conseguiu melhorar este texto. Reescreva com outras palavras e tente de novo.';

/** Código do backend quando o AI Gateway não respondeu (sem crédito, timeout); o backend já repetiu a chamada. */
const AI_UNAVAILABLE_CODE = 'AI_UNAVAILABLE';

class OptimizationRefusedError extends Error {
  constructor() {
    super(OPTIMIZE_REFUSAL_MESSAGE);
    this.name = 'OptimizationRefusedError';
  }
}

/** Repete só falha de transporte (sem resposta) ou 5xx genérico; recusa, 4xx e IA indisponível não mudam numa nova tentativa. */
function isTransient(err: unknown): boolean {
  if (err instanceof OptimizationRefusedError || !axios.isAxiosError(err)) return false;
  const res = err.response;
  if (!res) return true;
  const code = (res.data as { code?: unknown } | undefined)?.code;
  return res.status >= 500 && code !== AI_UNAVAILABLE_CODE;
}

export const optimizeText = async (
  sectorId: number,
  type: TextOptimizationType,
  text: string,
  agentId?: number,
  context?: string,
  capability?: OptimizationCapability
): Promise<string> => {
  const token = SessionService.getToken();
  if (!token) throw new Error(SESSION_EXPIRED_MESSAGE);

  let lastError: unknown = null;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const res = await axios.post<OptimizeResponse>(
        `${API_URL}/TextOptimization/optimize`,
        { sectorId, agentId, text, type, context, capability },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (res.data?.data?.refused === true) throw new OptimizationRefusedError();
      const optimized = res.data?.data?.optimizedText;
      if (res.data?.code !== 'SUCCESS' || !optimized) {
        throw new Error(OPTIMIZE_FALLBACK_MESSAGE);
      }
      return optimized;
    } catch (err) {
      lastError = err;
      if (err instanceof OptimizationRefusedError) throw err;
      if (attempt >= MAX_RETRIES || !isTransient(err)) break;
    }
  }
  throw new Error(apiErrorMessage(lastError, OPTIMIZE_FALLBACK_MESSAGE));
};
